import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { grantConsent, denyConsent, getConsent } from "@/lib/gtag";

/**
 * Minimal CCPA/GDPR-friendly cookie banner. Shows once for anonymous
 * visitors until they Accept or Decline; choice is stored in
 * localStorage["tbc.cookie_consent"] so it persists per-browser.
 *
 * Design intent: unobtrusive bottom-left card, doesn't hijack the whole
 * viewport. Accept boots GA4 immediately; Decline leaves tracking off.
 */
export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay by ~600 ms so the banner doesn't paint on top of the hero LCP
    // during first-load — hurts perceived speed and lighthouse scores.
    const t = setTimeout(() => {
      if (getConsent() == null) setVisible(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  const accept = () => {
    grantConsent();
    setVisible(false);
  };

  const decline = () => {
    denyConsent();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-4 right-4 bottom-4 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-sm z-[100] rounded-2xl bg-tbc-navy text-white shadow-2xl border border-tbc-gold/40 p-4 pr-3"
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-tbc-gold/15 flex items-center justify-center flex-none">
          <Cookie className="w-4 h-4 text-tbc-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-base leading-tight">Cookies &amp; analytics</p>
          <p className="text-xs text-white/75 mt-1 leading-relaxed">
            We use a light dusting of cookies (Google Analytics 4) to see which
            pages help businesses get discovered. No ads, no tracking pixels.
            You can opt out and still use every feature.{" "}
            <Link to="/privacy" className="underline text-tbc-gold" data-testid="cookie-banner-privacy-link">
              Privacy Policy
            </Link>
            .
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={accept}
              className="px-3 py-1.5 rounded-full bg-tbc-gold text-tbc-navy text-xs font-semibold hover:bg-tbc-gold/90"
              data-testid="cookie-banner-accept"
            >
              Accept
            </button>
            <button
              type="button"
              onClick={decline}
              className="px-3 py-1.5 rounded-full border border-white/25 text-white text-xs hover:bg-white/10"
              data-testid="cookie-banner-decline"
            >
              Decline
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={decline}
          className="p-1 rounded-full text-white/60 hover:text-white hover:bg-white/10"
          aria-label="Close and decline"
          data-testid="cookie-banner-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
